import voteForGame from "@casino/actions/Games/voteForGame";
import { useEffect, useState } from "react";

type Args = {
  gameId?: string;
  likes: number;
  isLiked: boolean;
};

const PLAY_FIRST_MESSAGE_TIMEOUT_MS = 4000;

// NOT_ALLOWED means the player hasn't bet yet; only that rejection surfaces needsPlayFirst.
const useVote = ({ gameId, likes, isLiked }: Args) => {
  const [vote, setVote] = useState({ likes, isLiked });
  const [isVoting, setIsVoting] = useState(false);
  const [needsPlayFirst, setNeedsPlayFirst] = useState(false);

  const submitVote = async () => {
    if (isVoting || !gameId) return;

    const previous = vote;
    const optimistic = previous.isLiked
      ? { likes: Math.max(previous.likes - 1, 0), isLiked: false }
      : { likes: previous.likes + 1, isLiked: true };

    setVote(optimistic);
    setIsVoting(true);
    setNeedsPlayFirst(false);

    const result = await voteForGame({
      gameId,
      rating: previous.isLiked ? undefined : "LIKE",
    });

    setIsVoting(false);

    if (result.errors.length) {
      setVote(previous);
      setNeedsPlayFirst(result.errors.includes("NOT_ALLOWED"));
      return;
    }

    setVote({
      likes: result.likes ?? optimistic.likes,
      isLiked: result.isLiked,
    });
  };

  const dismissPlayFirstMessage = () => setNeedsPlayFirst(false);

  useEffect(() => {
    if (!needsPlayFirst) return;
    const timeout = setTimeout(
      dismissPlayFirstMessage,
      PLAY_FIRST_MESSAGE_TIMEOUT_MS,
    );
    return () => clearTimeout(timeout);
  }, [needsPlayFirst]);

  return { ...vote, submitVote, needsPlayFirst, dismissPlayFirstMessage };
};

export default useVote;
